<div>
  <div class="container-fluid">
    <!-- Page Header with Health Indicator -->
    <div class="row mb-4">
      <div class="col-md-6">
        <div class="d-flex align-items-center gap-3">
          <h1 class="h2 mb-1 fw-bold">Agremo Credits</h1>
          <!-- Health Indicator -->
          <span *ngIf="!healthLoading" class="d-flex align-items-center gap-1">
            <span
              class="d-inline-block rounded-circle p-1"
              [class.bg-success]="healthStatus?.connected"
              [class.bg-danger]="!healthStatus?.connected && healthStatus?.configured !== false"
              [class.bg-warning]="healthStatus?.configured === false"
              [title]="healthStatus?.configured === false ? 'Agremo Not Configured' : (healthStatus?.connected ? 'Agremo API Connected' : 'Agremo API Disconnected')"
            ></span>
            <small
              [class.text-success]="healthStatus?.connected"
              [class.text-danger]="!healthStatus?.connected && healthStatus?.configured !== false"
              [class.text-warning]="healthStatus?.configured === false"
            >
              {{ healthStatus?.configured === false ? 'Not Configured' : (healthStatus?.connected ? 'Connected' : 'Disconnected') }}
            </small>
          </span>
          <span *ngIf="healthLoading" class="d-flex align-items-center gap-1">
            <div class="spinner-border spinner-border-sm text-muted" role="status">
              <span class="visually-hidden">Checking health...</span>
            </div>
            <small class="text-muted">Checking...</small>
          </span>
        </div>
        <p class="text-muted">Manage Agremo analysis credit blocks and monitor usage</p>
      </div>
      <div class="col-md-6 text-md-end">
        <button
          type="button"
          class="btn btn-outline-secondary me-2"
          (click)="confirmProcessPending()"
          [disabled]="processingPending"
        >
          <span *ngIf="processingPending" class="spinner-border spinner-border-sm me-2" role="status"></span>
          <i *ngIf="!processingPending" class="bi bi-play-circle me-2"></i>
          Process Pending
        </button>
        <button type="button" class="btn btn-primary" (click)="toggleAddForm()">
          <i class="bi bi-plus-circle me-2"></i>
          Add Credit Block
        </button>
      </div>
    </div>

    <!-- Success Message -->
    <div *ngIf="successMessage" class="alert alert-success alert-dismissible fade show" role="alert">
      <i class="bi bi-check-circle-fill me-2"></i>
      {{ successMessage }}
      <button type="button" class="btn-close" aria-label="Close" (click)="successMessage = ''"></button>
    </div>

    <!-- Error Message -->
    <div *ngIf="errorMessage" class="alert alert-danger alert-dismissible fade show" role="alert">
      <i class="bi bi-exclamation-triangle-fill me-2"></i>
      {{ errorMessage }}
      <button type="button" class="btn-close" aria-label="Close" (click)="errorMessage = ''"></button>
    </div>

    <!-- Not Configured Banner -->
    <div *ngIf="healthStatus?.configured === false" class="alert alert-warning" role="alert">
      <i class="bi bi-info-circle-fill me-2"></i>
      <strong>Agremo integration is not configured.</strong>
      Set the <code>AGREMO_CLIENT_ID</code>, <code>AGREMO_CLIENT_SECRET</code>, <code>AGREMO_USERNAME</code>, and <code>AGREMO_PASSWORD</code> environment variables to enable Agremo analysis features.
    </div>

    <!-- Usage Summary Cards -->
    <div class="row mb-4" *ngIf="!usageLoading">
      <div class="col-md-3">
        <div class="card">
          <div class="card-body text-center">
            <div class="entity-avatar entity-avatar-lg entity-avatar-square mx-auto bg-primary bg-opacity-10 text-primary mb-2">
              <i class="bi bi-credit-card-2-front"></i>
            </div>
            <!-- Dual-unit display: acres is canonical (matches the Stripe
                 per-acre model + our consumption code), hectares mirrors
                 Agremo's own dashboard for easy reconciliation. -->
            <h3 class="fs-2 fw-bold mb-1">{{ formatAcres(availableAcres) }} ac</h3>
            <small class="text-muted d-block">{{ formatHectares(availableHectares) }} ha · available</small>
            <small *ngIf="availableAnalyses > 0" class="text-muted d-block mt-1">
              + {{ availableAnalyses }} legacy analysis credit{{ availableAnalyses === 1 ? '' : 's' }}
            </small>
          </div>
        </div>
      </div>
      <div class="col-md-3">
        <div class="card">
          <div class="card-body text-center">
            <div class="entity-avatar entity-avatar-lg entity-avatar-square mx-auto bg-success bg-opacity-10 text-success mb-2">
              <i class="bi bi-check-circle"></i>
            </div>
            <h3 class="fs-2 fw-bold mb-1">{{ usageStats?.totalConsumed || 0 }}</h3>
            <small class="text-muted">Total Consumed</small>
          </div>
        </div>
      </div>
      <div class="col-md-3">
        <div class="card">
          <div class="card-body text-center">
            <div class="entity-avatar entity-avatar-lg entity-avatar-square mx-auto bg-info bg-opacity-10 text-info mb-2">
              <i class="bi bi-building"></i>
            </div>
            <h3 class="fs-2 fw-bold mb-1">{{ usageStats?.byOrganization?.length || 0 }}</h3>
            <small class="text-muted">Organizations Using</small>
          </div>
        </div>
      </div>
      <div class="col-md-3">
        <div class="card">
          <div class="card-body text-center">
            <div class="entity-avatar entity-avatar-lg entity-avatar-square mx-auto bg-warning bg-opacity-10 text-warning mb-2">
              <i class="bi bi-layers"></i>
            </div>
            <h3 class="fs-2 fw-bold mb-1">{{ usageStats?.byAnalysisType?.length || 0 }}</h3>
            <small class="text-muted">Analysis Types Used</small>
          </div>
        </div>
      </div>
    </div>

    <!-- Add Credit Block Form -->
    <div *ngIf="showAddForm" class="card mb-4">
      <div class="card-body">
        <h5 class="mb-3">
          <i class="bi bi-plus-circle me-2"></i>
          Add New Credit Block
        </h5>
        <div class="row g-3 align-items-end">
          <div class="col-md-3">
            <label class="form-label small text-body-secondary">Unit</label>
            <select class="form-select" [(ngModel)]="newCreditType">
              <option value="acres">Acres</option>
              <option value="hectares">Hectares (Agremo)</option>
            </select>
          </div>
          <div class="col-md-3">
            <label class="form-label small text-body-secondary">Amount</label>
            <input
              type="number"
              class="form-control"
              [placeholder]="newCreditType === 'hectares' ? 'Enter hectares' : 'Enter acres'"
              [(ngModel)]="newCreditAmount"
              min="0.01"
              step="0.01"
            />
            <!-- Live conversion preview so admins can sanity-check what
                 Agremo's HA value translates to in acres (and vice-versa). -->
            <small *ngIf="newCreditAmount && newCreditAmount > 0" class="text-muted">
              <ng-container *ngIf="newCreditType === 'hectares'">
                = {{ formatAcres(newCreditAmount * ACRES_PER_HECTARE) }} acres
              </ng-container>
              <ng-container *ngIf="newCreditType === 'acres'">
                = {{ formatHectares(newCreditAmount / ACRES_PER_HECTARE) }} ha
              </ng-container>
            </small>
          </div>
          <div class="col-md-3">
            <label class="form-label small text-body-secondary">Expiration Date</label>
            <input
              type="date"
              class="form-control"
              [(ngModel)]="newCreditExpiration"
            />
          </div>
          <div class="col-md-3">
            <button
              type="button"
              class="btn btn-primary me-2"
              (click)="submitNewCredit()"
              [disabled]="addingCredit || !newCreditAmount || newCreditAmount <= 0 || !newCreditExpiration"
            >
              <span *ngIf="addingCredit" class="spinner-border spinner-border-sm me-2" role="status"></span>
              {{ addingCredit ? 'Creating...' : 'Create' }}
            </button>
            <button type="button" class="btn btn-outline-secondary" (click)="toggleAddForm()">
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Loading State -->
    <div *ngIf="loading" class="text-center py-5">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
    </div>

    <!-- Active Credit Blocks -->
    <div *ngIf="!loading">
      <h5 class="mb-3">
        <i class="bi bi-credit-card me-2"></i>
        Active Credit Blocks
        <span class="badge bg-secondary ms-2">{{ activeCredits.length }}</span>
      </h5>

      <div *ngIf="activeCredits.length === 0" class="card overflow-hidden mb-4">
        <div class="card-body text-center py-5 text-muted">
          <i class="bi bi-credit-card-2-front fs-1 d-block mb-3"></i>
          <p>No active credit blocks. Add one to enable analysis requests.</p>
        </div>
      </div>

      <div class="card overflow-hidden mb-4" *ngIf="activeCredits.length > 0">
        <div class="table-responsive">
          <table class="table data-table table-hover mb-0">
            <thead>
              <tr>
                <th>Unit</th>
                <th>Total</th>
                <th>Remaining</th>
                <th>Used</th>
                <th>Usage</th>
                <th>Expires</th>
                <th>Created</th>
                <th class="text-end">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let credit of activeCredits">
                <td>
                  <span class="badge"
                    [class.bg-primary]="credit.creditType === 'acres'"
                    [class.bg-info]="credit.creditType === 'hectares'"
                    [class.bg-secondary]="credit.creditType === 'analysis'">
                    {{ unitLabel(credit) }}
                  </span>
                </td>
                <td class="fw-medium">{{ credit.totalCredits }}</td>
                <td>
                  <span [class.text-danger]="credit.remainingCredits <= 5">
                    {{ credit.remainingCredits }}
                  </span>
                  <!-- Show the cross-unit equivalent so admins can see at
                       a glance how much area is left on a hectare-typed
                       block vs. an acres-typed one. -->
                  <small *ngIf="credit.creditType === 'hectares'" class="text-muted d-block">
                    {{ formatAcres(blockRemainingAsAcres(credit)) }} ac
                  </small>
                  <small *ngIf="credit.creditType === 'acres'" class="text-muted d-block">
                    {{ formatHectares(blockRemainingAsHectares(credit)) }} ha
                  </small>
                </td>
                <td>{{ credit.totalCredits - credit.remainingCredits }}</td>
                <td style="min-width: 120px;">
                  <div class="progress" style="height: 6px;">
                    <div
                      class="progress-bar"
                      [ngClass]="getUsageBarClass(credit)"
                      role="progressbar"
                      [style.width.%]="getUsagePercent(credit)"
                    ></div>
                  </div>
                  <small class="text-muted">{{ getUsagePercent(credit) }}%</small>
                </td>
                <td>
                  <span [class.text-warning]="isExpiringSoon(credit)">
                    {{ formatDate(credit.expiresAt) }}
                    <i *ngIf="isExpiringSoon(credit)" class="bi bi-exclamation-triangle ms-1 text-warning"></i>
                  </span>
                </td>
                <td>{{ formatDate(credit.createdAt) }}</td>
                <td class="text-end">
                  <button
                    type="button"
                    class="btn btn-sm btn-outline-warning"
                    (click)="confirmArchive(credit)"
                    title="Archive"
                  >
                    <i class="bi bi-archive"></i>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Archived/Expired Credit Blocks -->
      <div class="mb-4">
        <button
          type="button"
          class="btn btn-sm btn-outline-secondary"
          (click)="showArchived = !showArchived"
        >
          <i class="bi" [ngClass]="showArchived ? 'bi-eye-slash' : 'bi-eye'"></i>
          {{ showArchived ? 'Hide' : 'Show' }} Archived/Expired
          <span class="badge bg-secondary ms-1">{{ archivedCredits.length }}</span>
        </button>
      </div>

      <div *ngIf="showArchived && archivedCredits.length > 0" class="card overflow-hidden mb-4" style="opacity: 0.7;">
        <div class="table-responsive">
          <table class="table data-table mb-0">
            <thead>
              <tr>
                <th>Total Credits</th>
                <th>Remaining</th>
                <th>Used</th>
                <th>Expires</th>
                <th>Status</th>
                <th>Created</th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let credit of archivedCredits">
                <td>{{ credit.totalCredits }}</td>
                <td>{{ credit.remainingCredits }}</td>
                <td>{{ credit.totalCredits - credit.remainingCredits }}</td>
                <td>{{ formatDate(credit.expiresAt) }}</td>
                <td>
                  <span *ngIf="credit.archivedAt" class="badge bg-secondary">Archived</span>
                  <span *ngIf="!credit.archivedAt" class="badge bg-warning text-dark">Expired</span>
                </td>
                <td>{{ formatDate(credit.createdAt) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Usage by Organization -->
      <ng-container *ngIf="usageStats">
        <div *ngIf="usageStats.byOrganization.length > 0" class="card mb-4">
          <div class="card-body">
            <h5 class="mb-3">
              <i class="bi bi-building me-2"></i>
              Usage by Organization
            </h5>
            <div class="table-responsive">
              <table class="table data-table mb-0">
                <thead>
                  <tr>
                    <th>Organization</th>
                    <th>Credits Consumed</th>
                    <th>Acres Analyzed</th>
                    <th>Hectares (Agremo)</th>
                    <th>Analyses</th>
                  </tr>
                </thead>
                <tbody>
                  <tr *ngFor="let org of usageStats.byOrganization">
                    <td class="fw-medium">{{ org.organizationName }}</td>
                    <td>{{ org.creditsConsumed }}</td>
                    <td>{{ formatAcres(org.acresAnalyzed || 0) }}</td>
                    <td class="text-muted">{{ formatHectares((org.acresAnalyzed || 0) / ACRES_PER_HECTARE) }}</td>
                    <td>{{ org.analysisCount }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <!-- Usage by Analysis Type -->
        <div *ngIf="usageStats.byAnalysisType.length > 0" class="card mb-4">
          <div class="card-body">
            <h5 class="mb-3">
              <i class="bi bi-layers me-2"></i>
              Usage by Analysis Type
            </h5>
            <div class="table-responsive">
              <table class="table data-table mb-0">
                <thead>
                  <tr>
                    <th>Analysis Type</th>
                    <th>Credits Consumed</th>
                    <th>Acres Analyzed</th>
                    <th>Hectares (Agremo)</th>
                    <th>Analyses</th>
                  </tr>
                </thead>
                <tbody>
                  <tr *ngFor="let type of usageStats.byAnalysisType">
                    <td class="fw-medium">{{ type.analysisTypeName || type.analysisType }}</td>
                    <td>{{ type.creditsConsumed }}</td>
                    <td>{{ formatAcres(type.acresAnalyzed || 0) }}</td>
                    <td class="text-muted">{{ formatHectares((type.acresAnalyzed || 0) / ACRES_PER_HECTARE) }}</td>
                    <td>{{ type.analysisCount }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </ng-container>

      <!-- Agremo API Codebooks (reference) -->
      <div class="card mb-4" *ngIf="codebooksLoading || codebooksError || analysisTypes.length > 0 || mapStatuses.length > 0 || cameraTypes.length > 0 || layerTypes.length > 0 || layerStatuses.length > 0">
        <div class="card-body">
          <h5 class="mb-3">
            <i class="bi bi-book me-2"></i>
            Agremo API Codebooks
          </h5>
          <div *ngIf="codebooksLoading" class="text-center py-2">
            <div class="spinner-border spinner-border-sm text-muted" role="status"></div>
            <small class="text-muted ms-2">Loading codebooks...</small>
          </div>
          <div *ngIf="codebooksError" class="text-danger small mb-2">{{ codebooksError }}</div>

          <div *ngIf="!codebooksLoading" class="row g-3">
            <!-- Analysis Types -->
            <div class="col-md-6 col-lg-3" *ngIf="analysisTypes.length > 0">
              <h6 class="text-muted mb-2">Analysis Types <span class="badge bg-secondary">{{ analysisTypes.length }}</span></h6>
              <table class="table data-table table-sm mb-0">
                <thead>
                  <tr>
                    <th style="width: 60px;">ID</th>
                    <th>Name</th>
                  </tr>
                </thead>
                <tbody>
                  <tr *ngFor="let type of analysisTypes">
                    <td><span class="badge bg-secondary">{{ type.id }}</span></td>
                    <td>{{ type.name }}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- Map Statuses -->
            <div class="col-md-6 col-lg-3" *ngIf="mapStatuses.length > 0">
              <h6 class="text-muted mb-2">Map Statuses</h6>
              <table class="table data-table table-sm mb-0">
                <thead>
                  <tr>
                    <th style="width: 60px;">Code</th>
                    <th>Description</th>
                  </tr>
                </thead>
                <tbody>
                  <tr *ngFor="let status of mapStatuses">
                    <td><span class="badge bg-secondary">{{ status.status_code }}</span></td>
                    <td>{{ status.status_description }}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- Camera Types -->
            <div class="col-md-6 col-lg-3" *ngIf="cameraTypes.length > 0">
              <h6 class="text-muted mb-2">Camera Types</h6>
              <table class="table data-table table-sm mb-0">
                <thead>
                  <tr>
                    <th style="width: 60px;">Code</th>
                    <th>Bands</th>
                  </tr>
                </thead>
                <tbody>
                  <tr *ngFor="let camera of cameraTypes">
                    <td><span class="badge bg-secondary">{{ camera.camera_code }}</span></td>
                    <td>{{ camera.camera_bands }}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- Layer Types -->
            <div class="col-md-6 col-lg-3" *ngIf="layerTypes.length > 0">
              <h6 class="text-muted mb-2">Layer Types</h6>
              <table class="table data-table table-sm mb-0">
                <thead>
                  <tr>
                    <th style="width: 60px;">Code</th>
                    <th>Description</th>
                  </tr>
                </thead>
                <tbody>
                  <tr *ngFor="let type of layerTypes">
                    <td><span class="badge bg-secondary">{{ type.layer_type_code }}</span></td>
                    <td>{{ type.layer_type_description }}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- Layer Statuses -->
            <div class="col-md-6 col-lg-3" *ngIf="layerStatuses.length > 0">
              <h6 class="text-muted mb-2">Layer Statuses</h6>
              <table class="table data-table table-sm mb-0">
                <thead>
                  <tr>
                    <th style="width: 60px;">Code</th>
                    <th>Description</th>
                  </tr>
                </thead>
                <tbody>
                  <tr *ngFor="let status of layerStatuses">
                    <td><span class="badge bg-secondary">{{ status.status_code }}</span></td>
                    <td>{{ status.status_description }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>

<!-- Archive Confirmation Modal -->
<app-confirmation-modal
  *ngIf="showConfirmModal"
  title="Archive Credit Block"
  [message]="'Are you sure you want to archive this credit block with ' + (archiveTargetCredits?.remainingCredits || 0) + ' remaining credits? The credits will no longer be available for consumption.'"
  confirmLabel="Archive"
  processingLabel="Archiving..."
  variant="warning"
  icon="bi bi-archive"
  [processing]="confirmModalProcessing"
  (confirmed)="onConfirmArchive()"
  (cancelled)="onCancelArchive()">
</app-confirmation-modal>

<!-- Process Pending Confirmation Modal -->
<app-confirmation-modal
  *ngIf="showProcessConfirmModal"
  title="Process Pending Analyses"
  message="This will attempt to process all pending analyses using available credits. Are you sure you want to proceed?"
  confirmLabel="Process"
  processingLabel="Processing..."
  variant="primary"
  icon="bi bi-play-circle"
  [processing]="processingPending"
  (confirmed)="onConfirmProcessPending()"
  (cancelled)="onCancelProcessPending()">
</app-confirmation-modal>
